import { useCallback, useEffect, useState } from 'react';
import {
  DEFAULT_SETTINGS,
  loadYogaSeries,
  preloadLoadingPreviews,
  VIEW_OPTIONS,
} from '../model/yogaModel';

const ANIMATION_DURATION = 300;
const MIN_LOADING_DURATION = 2500;

const wait = (duration) => new Promise((resolve) => {
  window.setTimeout(resolve, duration);
});

function useYogaPresenter() {
  const [activeSequence, setActiveSequence] = useState(null);
  const [imageSources, setImageSources] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selected, setSelected] = useState(DEFAULT_SETTINGS);
  const [animating, setAnimating] = useState({});
  const [isOptionsOpen, setIsOptionsOpen] = useState(false);

  useEffect(() => {
    let isMounted = true;

    Promise.all([
      preloadLoadingPreviews().then(loadYogaSeries),
      wait(MIN_LOADING_DURATION),
    ])
      .then(([series]) => {
        if (isMounted) setImageSources(series);
      })
      .finally(() => {
        if (isMounted) setIsLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setIsOptionsOpen(false);
  }, [activeSequence]);

  // Keep sequence navigation in sync with Safari's Back button and edge-swipe gesture.
  useEffect(() => {
    const handleHistoryNavigation = (event) => {
      const sequenceId = event.state?.yogaSequenceId;
      const sequence = sequenceId
        ? imageSources.find((item) => item.id === sequenceId)
        : null;

      setActiveSequence(sequence || null);
    };

    window.addEventListener('popstate', handleHistoryNavigation);
    return () => window.removeEventListener('popstate', handleHistoryNavigation);
  }, [imageSources]);

  const openSequence = useCallback((sequence) => {
    const state = { yogaSequenceId: sequence.id };

    if (activeSequence || window.history.state?.yogaSequenceId) {
      window.history.replaceState(state, '');
    } else {
      window.history.pushState(state, '');
    }

    setActiveSequence(sequence);
  }, [activeSequence]);

  const showHome = useCallback(() => {
    if (window.history.state?.yogaSequenceId) {
      window.history.back();
      return;
    }

    setActiveSequence(null);
  }, []);

  const selectOption = (groupId, option) => {
    setSelected((previous) => {
      const hasValidMode = ['AUTO', 'MANUAL'].includes(previous.mode);
      const next = {
        ...previous,
        [groupId]: option,
        ...(groupId === 'mode' ? { display: 'SHOW IN SEQUENCE' } : {}),
        ...(groupId === 'display' && option === 'SHOW IN SEQUENCE' && !hasValidMode
          ? { mode: 'MANUAL' }
          : {}),
      };

      if (previous[groupId] === option && previous.display === next.display) return previous;

      setAnimating((current) => ({
        ...current,
        [groupId]: { from: previous[groupId], to: option },
      }));

      window.setTimeout(() => {
        setAnimating((current) => {
          const next = { ...current };
          delete next[groupId];
          return next;
        });
      }, ANIMATION_DURATION);

      return next;
    });
  };

  return {
    activeSequence,
    animating,
    closeOptions: () => setIsOptionsOpen(false),
    imageSources,
    isLoading,
    isOptionsOpen,
    openSequence,
    options: VIEW_OPTIONS,
    selected,
    selectOption,
    showHome,
    toggleOptions: () => setIsOptionsOpen((isOpen) => !isOpen),
  };
}

export default useYogaPresenter;
